import {
  Model,
  UIButton,
  UICheckbox,
  UIDate,
  UIField,
  UIIconButton,
  UIImageButton,
  UIRadio,
  UIRange,
  UISelect,
  UISlot,
} from "@alphascriptsolutions/client-core";
import { getSharePricePerformanceData } from "./getSharePricePerformanceData";
import { ModelIndexes } from "../../types/addContent";
import { setupButtonGroup, updateButtonStates } from "../imageSelectButton";
import {
  backgroundShadingPanelSchema,
  callOutBoxesPanelSchema,
  companySharePriceCompaniesPanelSchema,
  constantLineSchema,
  contentLibrarySchema,
  customBuildIndexesSchema,
  customizeOptionsPanelSchema,
  formatTemplatesTablePanelSchema,
  getDerivedDataCalculationApproachSchema,
  getIndexExtendedSchema,
  getInputValueApproachSchema,
  getLineExtendedSchema,
  getPointInTimeStaticLineDateSchema,
  getTimeSeriesSchema,
  IindexationPanelSchema,
  initialInputPanelSchema,
  removeDynamicSchemasUpTo,
  smallLineChartPanelSchema,
  summaryTablePanelSchema,
  valueIndexationPanelSchema,
  verticalLinePanelSchema,
} from "./panelsSchema";
import dayjs, { Dayjs } from "dayjs";
import { useSharePricePerformanceStore } from "../../state/sharePricePerformanceStore";
import { addNewAnchor, setRangeInputDate } from "./panelHelpers";
import * as yup from "yup";
import { fetchPrimaryTemplateWithImage } from "./fetchFormatTemplates";
import { getSelectedShapeName, removeSelectedShape } from "./selectedPlaceHolderLocalStorage";
import {
  convertToRequiredFormat,
  getAllModelsData,
  setAllPanelsFieldValue,
} from "./getAllSharePricePerformancePanelsValues";
import { logger } from "../../helpers/tslog";
import { deleteAnchorChildren } from "../anchorChildManage";
import CurrencySelect from "../../components/CurrencySelect";
import { bufferToBase64ImagePrimaryTemplate } from "@/utils/formatTemplate/slides-extraction-handlers";

export async function addEventsOnModel(model: Model, index: number, forceUpdate) {
  const { setErrors, setModels, setTimePeriod, setFormCompleted, setOpenModelType, setAPALCompany } =
    useSharePricePerformanceStore.getState();

  const { indexationCompanyNames, indexationValue } = getSharePricePerformanceData();
  if (index === ModelIndexes.CONTENT_LIBRARY) {
    const imageButtonNames = [
      "sharePricePerformanceLineChart",
      "sharePricePerformanceColumnChart",
      "sharePricePerformanceBarChart",
      "sharePricePerformanceTable",
    ];
    imageButtonNames.forEach((name) => {
      const button = model.getByName<UIImageButton>(name);
      if (button) {
        button.onClick = async () => {
          updateButtonStates(imageButtonNames, model, button.name);
          forceUpdate();
        };
      }
    });
    model.setSchema(contentLibrarySchema);
  }

  if (index === ModelIndexes.TABLE_MODEL_INDEX) {
    const dateRange = model.getByName<UIRange>("timePeriodInitialInputs");
    dateRange.disabledDateRange = (current: Dayjs) => current?.isAfter(dayjs(), "day");

    // For Indexation
    const imageButtonNames = ["percentageChangeChart", "actualTradedValueChart"];
    setupButtonGroup(imageButtonNames, model, forceUpdate);

    // For Custom Build Value Line
    const imageButtonConstantValueLineNames = ["addCustomBuiltIndices", "noCustomBuiltIndices"];
    setupButtonGroup(imageButtonConstantValueLineNames, model, forceUpdate);

    // For Static Line
    const imageButtonStaticLineNames = ["addConstantLineSelection", "noConstantLineSelection"];
    setupButtonGroup(imageButtonStaticLineNames, model, forceUpdate);

    const currencySelectionSlot: any = model.getByName<UISlot>("currencySelectionSlot");
    if (currencySelectionSlot) {
      const currencyValue = localStorage.getItem("currencySelectionSlotValue") ?? "USD";
      currencySelectionSlot.value = currencyValue;
      currencySelectionSlot.setComponent(
        <CurrencySelect
          value={currencyValue}
          onSelect={(value) => {
            localStorage.setItem("currencySelectionSlotValue", value);
          }}
        />
      );
    }

    const timePeriodInitialInput = model.getByName<UIRange>("timePeriodInitialInputs");
    if (timePeriodInitialInput) {
      timePeriodInitialInput.onChange = (selectedPeriod: [string, string]) => {
        const { models } = useSharePricePerformanceStore.getState();
        setTimePeriod(selectedPeriod);
        setRangeInputDate(models[ModelIndexes.SMALL_LINE_CHART], "smallLineChartTimePeriod");
        const timeSeriesRanges =
          models[ModelIndexes.CONSTANT_LINE].uiComponents[0].getByPartialName("timePeriodTimeSeries");
        if (timeSeriesRanges && timeSeriesRanges.length > 0) {
          timeSeriesRanges.map((range) => {
            setRangeInputDate(models[ModelIndexes.CONSTANT_LINE], range.name);
          });
        }

        const pointInTimeDate: UIDate[] =
          models[ModelIndexes.CONSTANT_LINE].uiComponents[0].getByPartialName("staticLineDateInitialInput");
        if (pointInTimeDate && pointInTimeDate.length > 0) {
          pointInTimeDate.map((range) => {
            setRangeInputDate(models[ModelIndexes.CONSTANT_LINE], range.name);
          });
        }
        forceUpdate();
      };
    }

    model.getByName<UIButton>("addCompany")!.onClick = () => setOpenModelType("company");

    model.setSchema(initialInputPanelSchema);
  }

  if (index === ModelIndexes.INDEXATION_MODEL_INDEX) {
    const radioButton = model.getByName<UIRadio>("indexStartingPoint");
    model.setSchema(IindexationPanelSchema);

    if (radioButton) {
      if (radioButton.value === "Company Share Price") {
        addNewAnchor({
          fileName: "/assets/indexationChildPanels/CompanySharePrice.yml",
          model: model,
          anchorName: "indexationHideShow",
          modelIndex: index,
          callbackForNewModel: (newIndexModel: Model) => {
            const { company } = useSharePricePerformanceStore.getState();
            const radio = newIndexModel.getByName<UIRadio>("companyNames");
            radio.options = company.map((c) => c.companyNameAndTicker);
            model.setSchema(companySharePriceCompaniesPanelSchema);
          },
          setModels,
          datasource: indexationCompanyNames,
        });
      }
      if (radioButton.value === "Value") {
        addNewAnchor({
          fileName: "/assets/indexationChildPanels/Value.yml",
          model: model,
          anchorName: "indexationHideShow",
          modelIndex: index,
          datasource: indexationValue,
          callbackForNewModel: () => {
            model.setSchema(valueIndexationPanelSchema);
          },
          setModels,
        });
      }

      radioButton.onChange = (selectedValue: string) => {
        const { models } = useSharePricePerformanceStore.getState();
        const modelToUpdate = models[index];
        if (selectedValue === "Company Share Price") {
          addNewAnchor({
            fileName: "/assets/indexationChildPanels/CompanySharePrice.yml",
            model: modelToUpdate,
            anchorName: "indexationHideShow",
            modelIndex: index,
            callbackForNewModel: (newIndexModel: Model) => {
              const { company } = useSharePricePerformanceStore.getState();
              const radio = newIndexModel.getByName<UIRadio>("companyNames");
              radio.options = company.map((c) => c.companyNameAndTicker);
              modelToUpdate.setSchema(companySharePriceCompaniesPanelSchema);
            },
            setModels,
          });
        }

        if (selectedValue === "Value") {
          addNewAnchor({
            fileName: "/assets/indexationChildPanels/Value.yml",
            model: modelToUpdate,
            anchorName: "indexationHideShow",
            modelIndex: index,
            callbackForNewModel: () => {
              modelToUpdate.setSchema(valueIndexationPanelSchema);
            },
            setModels,
          });
        }
      };
    }
  }

  if (index === ModelIndexes.CUSTOM_BUILD_INDEX) {
    const { oldModelsData, indices } = getSharePricePerformanceData();
    model.setSchema(customBuildIndexesSchema);
    const radioComponent = model.getByName<UIRadio>("radioAddIndex");
    const constituentContributionApproach = model.getByName<UISelect>("constituentContributionApproach");
    constituentContributionApproach.value = constituentContributionApproach.options[0];
    addEventForBuildIndex(radioComponent, 0);
    model.getByName<UIButton>("addAPALCompany")!.onClick = () => handleAddAPALCompany(0);
    const count = Object.keys(oldModelsData[ModelIndexes.CUSTOM_BUILD_INDEX] ?? {}).length;
    for (let i = 0; i < count - 1; i++) {
      const datasource = indices[i];
      addNewAnchor({
        fileName: "/assets/custom_build_index/Index2.yml",
        model: model,
        anchorName: "indexContainer",
        modelIndex: ModelIndexes.CUSTOM_BUILD_INDEX,
        AnchorFirstClear: false,
        AnchorLimit: 4,
        panelIndex: i + 1,
        datasource,
        setModels,
        manipulateNewModel: (newModel) => {
          const updateTitele = newModel.getByName("index" + (+i + 1));
          updateTitele.title = "Create Index" + " " + (i + 2);
          if (i === 3) {
            newModel.uiComponents[0].children.splice(newModel.uiComponents[0].children.length - 2, 2);
          }

          return newModel;
        },
        callbackForNewModel: () => {
          const baseSchema = model.schema || yup.object();
          const extendedSchema = getIndexExtendedSchema(baseSchema, i);
          model.setSchema(extendedSchema);

          const radioComponentNew = model.getByName<UIRadio>("radioAddIndex" + (i + 1));
          const addAPALCompanyButton = model.getByName<UIButton>("addAPALCompany" + (i + 1));
          const constituentContributionApproach = model.getByName<UISelect>(
            "constituentContributionApproach" + (i + 1)
          );
          constituentContributionApproach.value = constituentContributionApproach.options[0];
          if (radioComponentNew) {
            addEventForBuildIndex(radioComponentNew, index + 1);
          }
          if (addAPALCompanyButton && !addAPALCompanyButton.onClick) {
            addAPALCompanyButton.onClick = () => handleAddAPALCompany(index + 1);
          }
        },
      });

      const custom = oldModelsData[ModelIndexes.CUSTOM_BUILD_INDEX] ?? {};

      const keys = Object.keys(custom)
        .filter((k) => k.startsWith("index"))
        .sort((a, b) => {
          const ia = a === "index" ? 0 : parseInt(a.replace("index", ""), 10);
          const ib = b === "index" ? 0 : parseInt(b.replace("index", ""), 10);
          return ia - ib;
        });

      const apalCompanyData: any[][] = keys.map((key) => {
        const section = custom[key];
        return section?.defineIndexContituents ?? [];
      });
      setAPALCompany(apalCompanyData);
    }
    // const modelsLocalStorage = localStorage.getItem("ModelData");
    // if (modelsLocalStorage) {
    //   const oldModelsData = JSON.parse(modelsLocalStorage);
    //   if (
    //     oldModelsData[ModelIndexes.CUSTOM_BUILD_INDEX] &&
    //     Object.keys(oldModelsData[ModelIndexes.CUSTOM_BUILD_INDEX]).length > 0
    //   ) {
    //     assignValueToCustomBuild(model, oldModelsData[ModelIndexes.CUSTOM_BUILD_INDEX]);
    //   }
    // }
  }

  if (index === ModelIndexes.CONSTANT_LINE) {
    const { oldModelsData, lines } = getSharePricePerformanceData();
    model.setSchema(constantLineSchema);

    const radioComponent = model.getByName("radioAddLine");
    addEventForConstantLine(radioComponent, 0);
    addDynamicDisplayForConstantLine(model, 0);

    const count = Object.keys(oldModelsData[ModelIndexes.CONSTANT_LINE] ?? {}).length;
    for (let index = 0; index < count - 1; index++) {
      const datasource = lines[index];
      addNewAnchor({
        fileName: "/assets/constant_line/Line2.yml",
        model: model,
        anchorName: "lineContainer",
        modelIndex: ModelIndexes.CONSTANT_LINE,
        AnchorFirstClear: false,
        AnchorLimit: 4,
        panelIndex: index + 1,
        datasource,
        setModels,
        manipulateNewModel: (newModel) => {
          const updateTitele = newModel.getByName("staticLine" + (+index + 1));
          updateTitele.title = "Create Line " + (index + 2);
          if (index === 3) {
            newModel.uiComponents[0].children.splice(newModel.uiComponents[0].children.length - 2, 2);
          }

          return newModel;
        },
        callbackForNewModel: () => {
          const baseSchema = model.schema || yup.object();
          const extendedSchema = getLineExtendedSchema(baseSchema, index);
          model.setSchema(extendedSchema);
          const radioComponentNew = model.getByName("radioAddLine" + (index + 1));
          addDynamicDisplayForConstantLine(model, index + 1);
          if (radioComponentNew) {
            addEventForConstantLine(radioComponentNew, index + 1);
          }
        },
      });
    }
  }

  if (index === ModelIndexes.CUSTOMIZE_OPTIONS) {
    // Question 1: Trading Volume
    const secondaryVerticalAxisButtonNames = [
      "addsecondaryVerticalAxisColumnChart",
      "nosecondaryVerticalAxisColumnChart",
    ];
    setupButtonGroup(secondaryVerticalAxisButtonNames, model, forceUpdate);

    // Question 2: Summary Table
    const summaryTableImageNames = ["addsummaryTable", "nosummaryTable"];
    setupButtonGroup(summaryTableImageNames, model, forceUpdate);

    // Question 3: Small Line Chart
    const smallLineChartImageNames = ["addSmallLineChart", "nosmallLineChart"];
    setupButtonGroup(smallLineChartImageNames, model, forceUpdate);

    // Question 4: Call Out Boxes
    const callOutBoxesImageNames = ["addCallOutBoxes", "noCallOutBoxes"];
    setupButtonGroup(callOutBoxesImageNames, model, forceUpdate);

    // Question 5: Background Shading
    const backgroundShadingImageNames = ["addBackgroundShading", "noBackgroundShading"];
    setupButtonGroup(backgroundShadingImageNames, model, forceUpdate);

    // Question 6: Vertical Line
    const verticalLineImageNames = ["addVerticalLine", "noVerticalLine"];
    setupButtonGroup(verticalLineImageNames, model, forceUpdate);

    model.setSchema(customizeOptionsPanelSchema);
  }

  if (index === ModelIndexes.SUMMARY_TABLE) {
    model.setSchema(summaryTablePanelSchema);
    const selectButton = model.getByName<UISelect>("rowHeadings");
    selectButton.value = "Companies / Indices";

    model.getByName<UIButton>("addSummaryTable")!.onClick = () => setOpenModelType("periodSummaryTable");
  }

  if (index === ModelIndexes.SMALL_LINE_CHART) {
    model.setSchema(smallLineChartPanelSchema);
  }

  if (index === ModelIndexes.CALL_OUT_BOXES) {
    model.setSchema(callOutBoxesPanelSchema);
    const addCompanyTestTableButton = model.getByName<UIButton>("addCallOutBox");
    if (addCompanyTestTableButton && !addCompanyTestTableButton.onClick) {
      addCompanyTestTableButton.onClick = () => setOpenModelType("CallOutBox");
    }
  }

  if (index === ModelIndexes.BACKGROUND_SHADING_TABLE_PANEL) {
    model.setSchema(backgroundShadingPanelSchema);
    const addCompanyTestTableButton = model.getByName<UIButton>("addShadingPeriodTableRow");
    if (addCompanyTestTableButton && !addCompanyTestTableButton.onClick) {
      addCompanyTestTableButton.onClick = () => setOpenModelType("ShadingPeriodTable");
    }
  }

  if (index === ModelIndexes.VERTICAL_LINE_TABLE_PANEL) {
    model.setSchema(verticalLinePanelSchema);
    const addCompanyTestTableButton = model.getByName<UIButton>("addVerticalLineTableRow");
    if (addCompanyTestTableButton && !addCompanyTestTableButton.onClick) {
      addCompanyTestTableButton.onClick = () => setOpenModelType("verticalLineTable");
    }
  }

  if (index === ModelIndexes.FORMAT_TEMPLATE) {
    model.setSchema(formatTemplatesTablePanelSchema);
    const primaryFormatWithImage = await fetchPrimaryTemplateWithImage();

    const { oldModelsData } = getSharePricePerformanceData();

    const presentationDesign = model.getByName("presentationDesign");
    if (presentationDesign) {
      try {
        // Check if there's a saved template selection
        const savedTemplate = localStorage.getItem("selectedFormatTemplate");
        let templateToShow = primaryFormatWithImage;
        let savedImage = null;

        if (savedTemplate) {
          try {
            const parsedTemplate = JSON.parse(savedTemplate);
            if (parsedTemplate.id && parsedTemplate.formatName) {
              // Use the saved template data for display
              templateToShow = {
                ...primaryFormatWithImage,
                id: parsedTemplate.id,
                formatName: parsedTemplate.formatName,
              };
              savedImage = parsedTemplate.image;
              logger.info("Restored saved template selection:", parsedTemplate.formatName);
            }
          } catch (parseError) {
            logger.error("Error parsing saved template:", parseError);
          }
        }

        if (templateToShow) {
          if (templateToShow.formatName) {
            (presentationDesign as any).templateName = templateToShow.formatName;
          }
          (presentationDesign as any).value = templateToShow.id;

          if (savedImage) {
            (presentationDesign as any).image = savedImage;
          } else if (templateToShow.frontCoverImage?.buffer) {
            try {
              (presentationDesign as any).image = bufferToBase64ImagePrimaryTemplate(
                templateToShow.frontCoverImage.buffer,
                templateToShow.frontCoverImage.contentType
              );
            } catch (e: any) {
              (presentationDesign as any).image = undefined;
            }
          } else {
            logger.info("No frontcover image found");
            (presentationDesign as any).image = undefined;
          }
          const { setTemplateSearchVisible, setCurrentPrimaryTemplate } = useSharePricePerformanceStore.getState();
          (presentationDesign as any).onClick = () => {
            setTemplateSearchVisible(true);
          };

          setCurrentPrimaryTemplate(templateToShow);
          localStorage.removeItem("selectedFormatTemplate");
        }
      } catch (error) {
        logger.error("Error populating format template selection:", error);
      }
    } else {
      logger.info("No presentationDesign component found");
    }
    const formatTemplateSource = model.getByName<UICheckbox>("formatTemplateSource");
    const formatTemplateNotes = model.getByName<UICheckbox>("formatTemplateNotes");
    formatTemplateSource.value = oldModelsData[ModelIndexes.FORMAT_TEMPLATE]?.source;
    formatTemplateNotes.value = oldModelsData[ModelIndexes.FORMAT_TEMPLATE]?.notes;

    const placeholderSelectionName = model.getByName<UIField>("placeholderSelectionName");
    const selectedPlaceholderName = getSelectedShapeName();
    placeholderSelectionName.value = selectedPlaceholderName ?? "";

    const placeholderSelectionBtn = model.getByName<UIIconButton>("placeholderSelectionBtn");

    placeholderSelectionBtn.onClick = async () => {
      const { models } = useSharePricePerformanceStore.getState();
      const allModelsData = setAllPanelsFieldValue(models);
      localStorage.setItem("ModelData", JSON.stringify(allModelsData));
      localStorage.removeItem("AlphaAgentPlaceholder");
      Office.context.ui.messageParent("OPEN_PLACEHOLDER");
    };

    const finishButton = model.getByName<UIButton>("btnFinish");

    finishButton.onClick = async () => {
      const e = await model.validate();
      if (e.length > 0) {
        setErrors(e);
        return;
      }
      const allModelsData = getAllModelsData();
      const newData = convertToRequiredFormat(allModelsData);
      localStorage.setItem("ModelDataToBackend", JSON.stringify(newData));
      removeSelectedShape();
      localStorage.removeItem("ModelData");
      localStorage.removeItem("PanelsData");
      localStorage.removeItem("currencySelectionSlotValue");
      setFormCompleted(true);
      Office.context.ui.messageParent("INSERT_CHART");
    };
    const allModelsData = localStorage.getItem("ModelData");
    localStorage.setItem("PanelsData", allModelsData);
    localStorage.removeItem("ModelData");
  }

  return model;
}

function addEventForBuildIndex(radioComponent, index) {
  const { setModels } = useSharePricePerformanceStore.getState();

  radioComponent.onChange = (selectedValue: "Yes" | "No") => {
    const { models } = useSharePricePerformanceStore.getState();
    radioComponent.selectedValue = selectedValue;
    const modelToUpdate = models[ModelIndexes.CUSTOM_BUILD_INDEX];
    const anchor = modelToUpdate.getByName("indexContainer");

    if (!anchor) {
      logger.error("Anchor 'indexContainer' not found in the Custom Build Index panel.");
      return;
    }
    if (selectedValue === "Yes") {
      addNewAnchor({
        fileName: "/assets/custom_build_index/Index2.yml",
        model: modelToUpdate,
        anchorName: "indexContainer",
        modelIndex: ModelIndexes.CUSTOM_BUILD_INDEX,
        AnchorFirstClear: false,
        AnchorLimit: 4,
        panelIndex: index + 1,
        setModels,
        manipulateNewModel: (newModel) => {
          const updateTitele = newModel.getByName("index" + (+index + 1));
          updateTitele.title = "Create Index" + " " + (index + 2);
          if (index === 3) {
            newModel.uiComponents[0].children.splice(newModel.uiComponents[0].children.length - 2, 2);
          }

          return newModel;
        },
        callbackForNewModel: () => {
          const baseSchema = modelToUpdate.schema || yup.object();
          const extendedSchema = getIndexExtendedSchema(baseSchema, index);
          modelToUpdate.setSchema(extendedSchema);

          const radioComponentNew = modelToUpdate.getByName("radioAddIndex" + (index + 1));
          const addAPALCompanyButton = modelToUpdate.getByName("addAPALCompany" + (index + 1));
          const constituentContributionApproach = modelToUpdate.getByName(
            "constituentContributionApproach" + (index + 1)
          );
          constituentContributionApproach.value = constituentContributionApproach.options[0];
          if (radioComponentNew) {
            addEventForBuildIndex(radioComponentNew, index + 1);
          }
          if (addAPALCompanyButton && !addAPALCompanyButton.onClick) {
            addAPALCompanyButton.onClick = () => handleAddAPALCompany(index + 1);
          }
        },
      });
    } else if (selectedValue === "No") {
      const children = deleteAnchorChildren(modelToUpdate, "indexContainer", index);
      modelToUpdate.setAtAnchor("indexContainer", children);

      const keysToRemove: string[] = [];

      for (let i = index + 1; i < 5; i++) {
        const suffix = i;
        keysToRemove.push(
          `indexName${suffix}`,
          `constituentContributionApproach${suffix}`,
          `defineIndexContituents${suffix}`,
          `radioAddIndex${suffix}`
        );
      }

      removeDynamicSchemasUpTo(modelToUpdate, keysToRemove);

      setModels((prev) => {
        const newModels = [...prev];
        newModels.splice(ModelIndexes.CUSTOM_BUILD_INDEX, 1, modelToUpdate);
        return newModels;
      });
    }
  };
}

function addEventForConstantLine(radioComponent, index) {
  const { setModels } = useSharePricePerformanceStore.getState();

  radioComponent.onChange = (selectedValue: "Yes" | "No") => {
    const { models } = useSharePricePerformanceStore.getState();
    radioComponent.selectedValue = selectedValue;
    const modelToUpdate = models[ModelIndexes.CONSTANT_LINE];
    const anchor = modelToUpdate.getByName("lineContainer");
    if (!anchor) {
      logger.error("Anchor 'lineContainer' not found in the Custom Build Index panel.");
      return;
    }
    if (selectedValue === "Yes") {
      addNewAnchor({
        fileName: "/assets/constant_line/Line2.yml",
        model: modelToUpdate,
        anchorName: "lineContainer",
        modelIndex: ModelIndexes.CONSTANT_LINE,
        AnchorFirstClear: false,
        AnchorLimit: 4,
        panelIndex: index + 1,
        setModels,
        manipulateNewModel: (newModel) => {
          const updateTitele = newModel.getByName("staticLine" + (+index + 1));
          updateTitele.title = "Create Line " + (index + 2);
          if (index === 3) {
            newModel.uiComponents[0].children.splice(newModel.uiComponents[0].children.length - 2, 2);
          }

          return newModel;
        },
        callbackForNewModel: () => {
          const baseSchema = modelToUpdate.schema || yup.object();
          const extendedSchema = getLineExtendedSchema(baseSchema, index);
          modelToUpdate.setSchema(extendedSchema);
          const radioComponentNew = modelToUpdate.getByName("radioAddLine" + (index + 1));
          addDynamicDisplayForConstantLine(modelToUpdate, index + 1);
          if (radioComponentNew) {
            addEventForConstantLine(radioComponentNew, index + 1);
          }
        },
      });

      setModels((prev) => {
        const newModels = [...prev];
        newModels.splice(ModelIndexes.CONSTANT_LINE, 1, modelToUpdate);
        return newModels;
      });
    } else if (selectedValue === "No") {
      const children = deleteAnchorChildren(modelToUpdate, "lineContainer", index);
      modelToUpdate.setAtAnchor("lineContainer", children);

      const keysToRemove: string[] = [];
      for (let i = index + 1; i < 5; i++) {
        const suffix = i;
        const suffix2 = i;
        keysToRemove.push(`lineName${suffix}`, `calculationApproach${suffix}`, `radioAddLine${suffix}`);
        keysToRemove.push(`derivedDataCalculationApproach${suffix2 > 0 ? suffix2 : ""}`);
        keysToRemove.push(`InputvalueApprach${suffix2 > 0 ? suffix2 : ""}`);
        keysToRemove.push(
          `staticLineDateInitialInput${suffix2 > 0 ? suffix2 : ""}`,
          `staticLine1Date${suffix2 > 0 ? suffix2 : ""}`,
          `radioCompanyNamesPointInTime${suffix2 > 0 ? suffix2 : ""}`
        );
        keysToRemove.push(
          `timePeriodTimeSeries${suffix2 > 0 ? suffix2 : ""}`,
          `CalculationRadio${suffix2 > 0 ? suffix2 : ""}`,
          `radioCompanyNamesTimeSeries${suffix2 > 0 ? suffix2 : ""}`
        );
      }
      removeDynamicSchemasUpTo(modelToUpdate, keysToRemove);
      setModels((prev) => {
        const newModels = [...prev];
        newModels.splice(ModelIndexes.CONSTANT_LINE, 1, modelToUpdate);
        return newModels;
      });
    }
  };
}

function addDynamicDisplayForConstantLine(model, index) {
  const { setModels } = useSharePricePerformanceStore.getState();
  const { values, derivedValueApproach } = getSharePricePerformanceData();
  const selectButton = model.getByName("calculationApproach" + (index > 0 ? index : ""));
  if (!selectButton) return;
  if (selectButton.value === "" || selectButton.value === "Input Value") {
    selectButton.value = "Input Value";
    const datasource = values[index];
    addNewAnchor({
      fileName: "/assets/constant_line/approach/InputValue.yml",
      model: model,
      anchorName: "approach" + (index > 0 ? index : ""),
      modelIndex: ModelIndexes.CONSTANT_LINE,
      panelIndex: index,
      datasource,
      setModels,
      callbackForNewModel: () => {
        const baseSchema = model.schema || yup.object();
        const extendedSchema = getInputValueApproachSchema(baseSchema, index);
        model.setSchema(extendedSchema);
        const keysToRemove: string[] = [];

        keysToRemove.push(`derivedDataCalculationApproach${index > 0 ? index : ""}`);

        removeDynamicSchemasUpTo(model, keysToRemove);
      },
    });
  }
  if (selectButton.value === "Derived Value") {
    const datasource = derivedValueApproach[index];
    addNewAnchor({
      fileName: "/assets/constant_line/approach/DerivedValue.yml",
      model: model,
      anchorName: "approach" + (index > 0 ? index : ""),
      modelIndex: ModelIndexes.CONSTANT_LINE,
      panelIndex: index,
      datasource,
      setModels,
      callbackForNewModel: (newIndexModel) => {
        const baseSchema = model.schema || yup.object();
        const extendedSchema = getDerivedDataCalculationApproachSchema(baseSchema, index);
        model.setSchema(extendedSchema);

        const keysToRemove: string[] = [];

        keysToRemove.push(`InputvalueApprach${index > 0 ? index : ""}`);

        removeDynamicSchemasUpTo(model, keysToRemove);

        const customizationCompaniesOptions = newIndexModel.uiComponents[0].getByPartialName("radioCompanyNames") as
          | UIRadio[]
          | undefined;

        addDerivedValueToggleRadioButton(index, model);
        if (customizationCompaniesOptions) {
          customizationCompaniesOptions.forEach((radio) => {
            const { company } = useSharePricePerformanceStore.getState();
            radio.options = company.map((c) => c.companyNameAndTicker);
          });
        }
      },
    });
  }

  selectButton.onSelect = (selectedValue: "Input Value" | "Derived Value") => {
    if (selectedValue === "Input Value") {
      addNewAnchor({
        fileName: "/assets/constant_line/approach/InputValue.yml",
        model: model,
        anchorName: "approach" + (index > 0 ? index : ""),
        modelIndex: ModelIndexes.CONSTANT_LINE,
        panelIndex: index,
        setModels,
        callbackForNewModel: () => {
          const baseSchema = model.schema || yup.object();
          const extendedSchema = getInputValueApproachSchema(baseSchema, index);
          model.setSchema(extendedSchema);
          const keysToRemove: string[] = [];

          keysToRemove.push(`derivedDataCalculationApproach${index > 0 ? index : ""}`);
          keysToRemove.push(
            `staticLineDateInitialInput${index > 0 ? index : ""}`,
            `staticLine1Date${index > 0 ? index : ""}`,
            `radioCompanyNamesPointInTime${index > 0 ? index : ""}`
          );
          keysToRemove.push(
            `timePeriodTimeSeries${index > 0 ? index : ""}`,
            `CalculationRadio${index > 0 ? index : ""}`,
            `radioCompanyNamesTimeSeries${index > 0 ? index : ""}`
          );

          removeDynamicSchemasUpTo(model, keysToRemove);
        },
      });
    } else if (selectedValue === "Derived Value") {
      addNewAnchor({
        fileName: "/assets/constant_line/approach/DerivedValue.yml",
        model: model,
        anchorName: "approach" + (index > 0 ? index : ""),
        modelIndex: ModelIndexes.CONSTANT_LINE,
        panelIndex: index,
        setModels,
        callbackForNewModel: (newIndexModel) => {
          const baseSchema = model.schema || yup.object();
          const extendedSchema = getDerivedDataCalculationApproachSchema(baseSchema, index);
          model.setSchema(extendedSchema);

          const keysToRemove: string[] = [];

          keysToRemove.push(`InputvalueApprach${index > 0 ? index : ""}`);

          removeDynamicSchemasUpTo(model, keysToRemove);

          const customizationCompaniesOptions = newIndexModel.uiComponents[0].getByPartialName("radioCompanyNames") as
            | UIRadio[]
            | undefined;

          addDerivedValueToggleRadioButton(index, model);
          if (customizationCompaniesOptions) {
            customizationCompaniesOptions.forEach((radio) => {
              const { company } = useSharePricePerformanceStore.getState();
              radio.options = company.map((c) => c.companyNameAndTicker);
            });
          }
        },
      });
    }
  };
}

function addDerivedValueToggleRadioButton(index: number, mainModel: Model) {
  const { setModels } = useSharePricePerformanceStore.getState();

  const derivedValueToggleRadioButton = mainModel.getByName<UIRadio>(
    "derivedDataCalculationApproach" + (index > 0 ? index : "")
  );
  const { derivedValueTimeSeries, derivedValuePointInTime } = getSharePricePerformanceData();

  if (derivedValueToggleRadioButton.value === "Time Series") {
    const datasource = derivedValueTimeSeries[index];
    addNewAnchor({
      fileName: "/assets/constant_line/approach/derived_value/TimeSeries.yml",
      model: mainModel,
      anchorName: "derivedValueToggle" + (index > 0 ? index : ""),
      modelIndex: ModelIndexes.CONSTANT_LINE,
      panelIndex: index,
      datasource,
      setModels,
      manipulateNewModel: (newModel: Model) => {
        setRangeInputDate(newModel, "timePeriodTimeSeries");
        setCompaniesInSelect(newModel, "radioCompanyNamesTimeSeries");

        return newModel;
      },
      callbackForNewModel: () => {
        const baseSchema = mainModel.schema ?? yup.object();
        const extendedSchema = getTimeSeriesSchema(baseSchema, index);
        mainModel.setSchema(extendedSchema);
        const keysToRemove: string[] = [];

        keysToRemove.push(
          `staticLineDateInitialInput${index > 0 ? index : ""}`,
          `staticLine1Date${index > 0 ? index : ""}`,
          `radioCompanyNamesPointInTime${index > 0 ? index : ""}`
        );

        removeDynamicSchemasUpTo(mainModel, keysToRemove);
      },
    });
  }

  if (derivedValueToggleRadioButton.value === "Point-in-Time") {
    const datasource = derivedValuePointInTime[index];
    addNewAnchor({
      fileName: "/assets/constant_line/approach/derived_value/PointInTime.yml",
      model: mainModel,
      anchorName: "derivedValueToggle" + (index > 0 ? index : ""),
      modelIndex: ModelIndexes.CONSTANT_LINE,
      AnchorFirstClear: true,
      panelIndex: index,
      datasource,
      setModels,
      manipulateNewModel: (newModel: Model) => {
        setRangeInputDate(newModel, "staticLineDateInitialInput");
        setCompaniesInSelect(newModel, "radioCompanyNamesPointInTime");
        return newModel;
      },
      callbackForNewModel: () => {
        const baseSchema = mainModel.schema || yup.object();
        const extendedSchema = getPointInTimeStaticLineDateSchema(baseSchema, index);
        mainModel.setSchema(extendedSchema);
        const keysToRemove: string[] = [];

        keysToRemove.push(
          `timePeriodTimeSeries${index > 0 ? index : ""}`,
          `CalculationRadio${index > 0 ? index : ""}`,
          `radioCompanyNamesTimeSeries${index > 0 ? index : ""}`
        );

        removeDynamicSchemasUpTo(mainModel, keysToRemove);
      },
    });
  }

  derivedValueToggleRadioButton.onChange = (selectedValue: "Time Series" | "Point-in-Time") => {
    if (selectedValue === "Time Series") {
      addNewAnchor({
        fileName: "/assets/constant_line/approach/derived_value/TimeSeries.yml",
        model: mainModel,
        anchorName: "derivedValueToggle" + (index > 0 ? index : ""),
        modelIndex: ModelIndexes.CONSTANT_LINE,
        panelIndex: index,
        setModels,
        manipulateNewModel: (newModel: Model) => {
          setRangeInputDate(newModel, "timePeriodTimeSeries");
          setCompaniesInSelect(newModel, "radioCompanyNamesTimeSeries");

          return newModel;
        },
        callbackForNewModel: (newIndexModel: Model) => {
          const CalculationRadio = newIndexModel.getByName<UIRadio>("CalculationRadio" + (index > 0 ? index : ""));
          CalculationRadio.value = CalculationRadio.options[0];
          const radioCompanyNamesTimeSeries = newIndexModel.getByName<UIRadio>(
            "radioCompanyNamesTimeSeries" + (index > 0 ? index : "")
          );
          radioCompanyNamesTimeSeries.value = radioCompanyNamesTimeSeries.options[0];

          const baseSchema = mainModel.schema ?? yup.object();
          const extendedSchema = getTimeSeriesSchema(baseSchema, index);
          mainModel.setSchema(extendedSchema);
          const keysToRemove: string[] = [];

          keysToRemove.push(
            `staticLineDateInitialInput${index > 0 ? index : ""}`,
            `staticLine1Date${index > 0 ? index : ""}`,
            `radioCompanyNamesPointInTime${index > 0 ? index : ""}`
          );

          removeDynamicSchemasUpTo(mainModel, keysToRemove);
        },
      });
    } else if (selectedValue === "Point-in-Time") {
      addNewAnchor({
        fileName: "/assets/constant_line/approach/derived_value/PointInTime.yml",
        model: mainModel,
        anchorName: "derivedValueToggle" + (index > 0 ? index : ""),
        modelIndex: ModelIndexes.CONSTANT_LINE,
        AnchorFirstClear: true,
        panelIndex: index,
        setModels,
        manipulateNewModel: (newModel: Model) => {
          setRangeInputDate(newModel, "staticLineDateInitialInput");
          setCompaniesInSelect(newModel, "radioCompanyNamesPointInTime");
          return newModel;
        },
        callbackForNewModel: (newIndexModel: Model) => {
          const radioCompanyNamesPointInTime = newIndexModel.getByName<UIRadio>(
            "radioCompanyNamesPointInTime" + (index > 0 ? index : "")
          );
          radioCompanyNamesPointInTime.value = radioCompanyNamesPointInTime.options[0];

          const baseSchema = mainModel.schema || yup.object();
          const extendedSchema = getPointInTimeStaticLineDateSchema(baseSchema, index);
          mainModel.setSchema(extendedSchema);
          const keysToRemove: string[] = [];

          keysToRemove.push(
            `timePeriodTimeSeries${index > 0 ? index : ""}`,
            `CalculationRadio${index > 0 ? index : ""}`,
            `radioCompanyNamesTimeSeries${index > 0 ? index : ""}`
          );

          removeDynamicSchemasUpTo(mainModel, keysToRemove);
        },
      });
    }
  };
}

function setCompaniesInSelect(model: Model, selectButtonName) {
  const selectButtons = model.uiComponents[0].getByPartialName<UISelect>(selectButtonName);
  if (!selectButtons) return;
  selectButtons.forEach((selectButton) => {
    const { company } = useSharePricePerformanceStore.getState();
    selectButton.options = company.map((c) => c.companyNameAndTicker);
  });
}

const handleAddAPALCompany = (index: number) => {
  const { setOpenModelType, setAPALCompanyIndex } = useSharePricePerformanceStore.getState();

  setAPALCompanyIndex(index);
  setOpenModelType("APALCompany");
};
